import {BaseElement, BASIS, isEltRefreshable, OLabelInit, OSkinableInit} from "back/commons/basis";
import {ACTION, Action, EButtonUiContext, IAction} from "lib/commons/actions";
import {IRegPointer} from 'lib/commons/registry';
import {isRefreshHook} from "lib/commons/utils/signboard";
import {DOM, INodeFilter, JSX} from "lib/commons/xml/dom";


/**
 * Class de base pour les widgets actionables comme des boutons.
 *
 * Attributes : cf BaseElement +
 * State refresh attributes: disabled, hidden
 * State init attributes: label, icon, tabindex-custom
 * Config init attribute : action, action-context
 */
export interface Actionable<C> extends BaseElement {
	initialize(init?: OActionableInit<C>): this;
}

/* Utilisation de Partial<IClickable> à la place de IClickableInit car disabled ne dépend pas uniquement de l'attribut sur Actionable */
export interface OActionableInit<C> extends OLabelInit {
	action?: IAction<C>
	actionContext?: C
	disabled?: boolean
	tabIndexCustom?: number
}

export abstract class Actionable<C> extends BaseElement {
	protected _label: string;

	protected _ariaLabel: string;
	protected _icon: string;
	protected _title: string;
	protected _disabled: boolean;
	protected _hidden: boolean;
	protected _action: IAction<C>;
	protected _actionContext: C;

	get icon(): string {
		if (this._icon) return this._icon;
		if (this._action) return this._action.getIcon(this._actionContext);
		return null;
	}

	set icon(val: string) {
		if (this._icon !== val) {
			this._icon = val;
			this.refresh();
		}
	}

	get label(): string {
		if (this._label != null) return this._label;
		if (this._action) return this._action.getLabel(this._actionContext);
		return null;
	}

	set label(val: string) {
		if (this._label != val) {
			this._label = val;
			this.refresh();
		}
	}

	get title(): string {
		if (this._title != null) return this._title;
		if (this._action) return this._action.getDescription(this._actionContext);
		return null;
	}

	set title(val: string) {
		if (this._title != val) {
			this._title = val;
			this.refresh();
		}
	}

	/** Label destiné à fournir le label de ce bouton pour les lecteurs d'écran */
	get ariaLabel(): string {
		if (this._ariaLabel != null) return this._ariaLabel;
		const label = this.label;
		return label || this.title;
	}

	set ariaLabel(val: string) {
		if (this._ariaLabel != val) {
			this._ariaLabel = val;
			this.refresh();
		}
	}

	tabIndexCustom: number;

	get disabled(): boolean {
		if (this._disabled != null) return this._disabled;
		if (this._action) return !this._action.isEnabled(this._actionContext);
		return false;
	}

	set disabled(val: boolean | null) {
		if (this._disabled !== val) {
			this._disabled = val;
			this.refresh();
		}
	}

	get hidden(): boolean {
		if (this._hidden != null) return this._hidden;
		if (this._action) return !this._action.isVisible(this._actionContext);
		return true;
	}

	set hidden(val: boolean | null) {
		if (this._hidden !== val) {
			this._hidden = val;
			this.refresh();
		}
	}

	get action(): IAction<C> {return this._action}

	get actionContext(): C {return this._actionContext}

	/** Pour une action de type IActionToggle, retourne son statut 'on'. */
	get toggleOn(): boolean | null | undefined {
		if (!this._action || !this._action.isToggle(this._actionContext)) return undefined;
		return this._action.getDatas('toggle', this._actionContext);
	}

	protected _iconElt: HTMLElement;
	protected _labelElt: HTMLElement;

	protected _initialize(init: OActionableInit<C>) {
		BASIS.makeClickable(this);
		this._icon = init.icon;
		this._label = init.label;
		this._title = init.title;
		this._disabled = init.disabled;
		this._hidden = init.hidden;
		this.tabIndexCustom = init.tabIndexCustom || 0;
		this._action = init.action;
		this._actionContext = init.actionContext;
		this._action.initButtonNode(this, this._actionContext);
		if (!this.onclick) {
			//pas d'attribut onclick surchargé
			this.onclick = this._executeAction;
		}
	}

	buildInitFromAtts(init?: OActionableInit<C>): OActionableInit<C> {
		init = super.buildInitFromAtts(init);
		init.actionContext = ACTIONABLES.extractActionContextFromDom<C>(this, init);
		init.action = ACTIONABLES.extractActionFromDom<C>(this, init);
		if (this.hasAttribute('tabindex-custom')) init.tabIndexCustom = BASIS.extractAttrAsNumber(this, 'tabindex-custom', 0);
		if (this.hasAttribute('label')) init.label = BASIS.extractAttr(this, 'label');
		if (this.hasAttribute('icon')) init.icon = BASIS.extractAttr(this, 'icon');
		if (this.hasAttribute('disabled')) init.disabled = this.getAttribute('disabled') !== 'false';
		if (this.hasAttribute('hidden')) init.hidden = this.getAttribute('hidden') !== 'false';
		return init;
	}

	/**
	 * Change l'action et son contexte associé à ce widget. Attention, à utiliser avec précaution, certains widgets
	 * pourraient mal gérer ce changement dynamique, l'action et l'actionContext étant en prinicpe des propriétés statiques
	 * de config et non des states.
	 */
	resetAction(action: Action<C>, ctx: C) {
		this._action = action;
		this._actionContext = ctx;
		this.refresh();
	}

	/** L'implémentation de base ne fait que dispatcher l'event c-actioned. Les sous-classes peuvent exécuter l'action. */
	protected _executeAction(ev: Event) {
		//if (this._action) this._action.executeIfAvailable(this._actionContext, ev);
		if (this._action.isAvailable(this._actionContext)) this.dispatchEvent(new CustomEvent('c-actioned', {detail: {actionable: this}, bubbles: true, composed: true}));
	}

	/** création de l'icon à la demande. */
	protected _createIcon() {
		this._iconElt = this.shadowRoot.appendChild(<span class='icon'/>);
	}

	/** création du label à la demande. */
	protected _createLabel() {
		this._labelElt = this.shadowRoot.appendChild(<span class='label'/>);
	}

	protected _refresh() {
		const hidden = this.hidden;
		DOM.setHidden(this, hidden);
		if (hidden) return;

		const label = this.label;
		const title = this.title;
		const icon = this.icon;

		if (icon) {
			if (!this._iconElt) this._createIcon();
			if (this._lastUrlIcon !== icon) {
				//NOTE : DOM.setStyle() avec une url ne marche pas à cause des escapings.
				this._lastUrlIcon = icon;
				this.style.setProperty('--icon-img', icon.startsWith('--') ? `var(${icon})` : `url("${icon}")`);
			}

			// accessibilité (indispensable si le label n'est pas affiché sur le bouton. ex : toolbar, ...)
			// NOTE @title ne semble pas pris en compte en fallback pour caractériser le bouton
			DOM.setAttr(this, "aria-label", this.ariaLabel);

			DOM.setHidden(this._iconElt, false);
		} else {
			if (this._iconElt) DOM.setHidden(this._iconElt, true);
		}

		if (label != null) {
			if (!this._labelElt) this._createLabel();
			DOM.setTextContent(this._labelElt, label);
			DOM.setHidden(this._labelElt, false);
		} else {
			if (this._labelElt) DOM.setHidden(this._labelElt, true);
		}

		DOM.setAttr(this, 'title', title);

		const disabled = this.disabled;
		DOM.setAttrBool(this, 'disabled', disabled);

		DOM.setAttr(this, "tabindex", disabled ? null : this.tabIndexCustom === -2 ? null : (this.tabIndexCustom || 0).toString());

		//Action de type Toggle
		const toggleOn = this.toggleOn;
		DOM.setAttr(this, 'aria-pressed', toggleOn === undefined ? null : toggleOn === null ? 'mixed' : toggleOn.toString());

	}

	protected _lastUrlIcon: string;
}

/**
 * Classe de base des toolbars, popup menu, etc. gérant une liste d'actions.
 *
 * Attributes : cf BaseElement +
 * Config init attribute : action-context, ui-context, group-order
 * State init attributes : actions
 */
export interface ActionableList<C> extends BaseElement {
	initialize(init: OActionableListInit<C>): this;
}

/** Etats pour widgets gérant une liste d'actions. */

/** Etats pour widgets gérant une liste d'actions. */
export interface OActionableListInit<C> extends OSkinableInit {
	actions?: IAction<C>[]
	actionContext?: C
	uiContext?: EButtonUiContext
	groupOrder?: string
}

/** Widgets gérant une liste d'actions. */
export class ActionableList<C> extends BaseElement implements IActionableList<C>{
	uiContext: EButtonUiContext;

	groupOrder: string;

	protected _actions: IAction<C>[];
	protected _actionContext: C;

	get actions(): IAction<C>[] {return this._actions}

	set actions(actions: IAction<C>[]) {
		if (this._actions !== actions) {
			this._actions = this.groupOrder == null ? actions : ACTION.injectSepByGroup(actions, this.groupOrder, this._actionContext);
			this.refresh();
		}
	}

	get actionContext(): C {return this._actionContext}

	/**
	 * Rafraichit chaque bouton existant, sans reconstruire la liste des actions.
	 */
	refreshContent() {
		if (isRefreshHook(this._actionContext) && this._actionContext.onRefreshCycle(true) === 'stop') return;
		try {
			DOM.findNext(this, this, (elt: Node): elt is Node => {
				if (isEltRefreshable(elt)) elt.refresh();
				return false;
			});
			ACTIONABLES.cleanupDoubleSep(this);
		} finally {
			if (isRefreshHook(this._actionContext)) this._actionContext.onRefreshCycle(false);
		}
	}

	protected _initialize(init: OActionableListInit<C>) {
		this._actionContext = init.actionContext;
		this.uiContext = init.uiContext || 'bar';
		this.groupOrder = init.groupOrder;
		this.actions = init.actions;
	}

	buildInitFromAtts(init?: OActionableListInit<C>): OActionableListInit<C> {
		init = super.buildInitFromAtts(init);
		init.actions = ACTIONABLES.extractActionsFromDom(this, init);
		init.actionContext = ACTIONABLES.extractActionContextFromDom(this, init);
		if (this.hasAttribute('ui-context')) init.uiContext = BASIS.extractAttr(this, "ui-context");
		if (this.hasAttribute('group-order')) init.groupOrder = BASIS.extractAttr(this, "group-order");
		return init;
	}

	protected _refresh() {
		if (!this._actionContext) {
			this.textContent = null;
			return;
		}
		if (isRefreshHook(this._actionContext) && this._actionContext.onRefreshCycle(true) === 'stop') return;
		try {
			//this.textContent = null; //désactivation refresh par diff
			let nextTag = this.firstElementChild;
			if (this._actions) for (let i = 0; i < this._actions.length; i++) {
				const action = this._actions[i];
				if (IS_Actionable(nextTag) && nextTag.action === action) {
					if (isEltRefreshable(nextTag)) nextTag.refresh();
					nextTag = nextTag.nextElementSibling;
				} else {
					const b = this._createButton(action);
					if (b) this.insertBefore(b, nextTag);
				}
			}
			while (nextTag) {
				const toRem = nextTag;
				nextTag = nextTag.nextElementSibling;
				toRem.remove();
			}
			ACTIONABLES.cleanupDoubleSep(this);
		} finally {
			if (isRefreshHook(this._actionContext)) this._actionContext.onRefreshCycle(false);
		}
	}

	protected _createButton(action: IAction<C>): Element {throw Error("To implement")}

}


/** Représente tout widget susceptible d'être vu comme un bouton actionable. */
export interface IActionable<C> extends HTMLElement {
	action: IAction<C>
	actionContext: C
	title: string
	hidden: boolean
	disabled: boolean
	uiContext?: EButtonUiContext

	click(): void
}


export const IS_Actionable = ((n: any): n is IActionable<any> => n && n.action) as INodeFilter<IActionable<any>>;
export const IS_ActionableEnabled = ((n: any): n is IActionable<any> => n && n.action && !n.hidden && !n.disabled) as INodeFilter<IActionable<any>>;


/**
 * Représente un ensemble de boutons à insérer dans une barre d'outils commune (cf BarShared).
 * Interface implémentée par :
 * - CustomEvent.detail pour l'event 'c-focus-actions' pour forcer une mse à jour de la toolbar.
 * - les widgets HTML. Le 1er widget de la hiérarchie retournée par Event.composedPath() lors
 * d'un event 'focusin' et définissant la propriété 'focusActionables' sera utilisé pour construire la toolbar.
 */
export interface IFocusActionables<C> {
	focusActionables?: IActionable<C>[]
}


/**
 *
 */
export interface IContextMenuActionsPointer<C> {
	ctxMenuActions?: IContextMenuActions<C> | null
}

export interface IContextMenuActions<C> {
	actions: IAction<C>[]
	actionContext: C
	/** Spécifie la zone associée à ce menu contextuel. */
	rect?: DOMRectReadOnly;
}

export interface IActionableList<C> extends HTMLElement {
	actions: IAction<C>[]
	readonly actionContext: C
}

export namespace ACTIONABLES {

	/**
	 * Après un refresh d'un ensemble d'actions, masque les séparateurs consécutifs ou au début/fin
	 */
	export function cleanupDoubleSep(this: void, root: HTMLElement) {
		let prevWdgt = false;
		let lastSep: IActionable<any> = null;
		for (let ch = root.firstElementChild as HTMLElement; ch; ch = ch.nextElementSibling as HTMLElement) {
			if (IS_Actionable(ch) && ch.action.getId() === '#SEP') {
				DOM.setHidden(ch, !prevWdgt);
				prevWdgt = false;
				lastSep = ch;
			} else if (!prevWdgt && !ch.hidden) {
				prevWdgt = true;
			}
		}
		if (!prevWdgt && lastSep)
			DOM.setHidden(lastSep, !prevWdgt);
	}

	export function extractActionFromDom<C>(elt: BaseElement, init: IRegPointer<any>): Action<C> {
		const action = elt.getAttribute('action');
		return action ? elt.findReg(init).getSvc<Action<C>>(action) : null;
	}

	export function extractActionContextFromDom<C>(elt: BaseElement, init: IRegPointer<any>): C {
		const actionCtx = elt.getAttribute('action-context');
		//Si pas de contexte => contexte == le bouton.
		return actionCtx ? elt.findReg(init).getSvc<C>(actionCtx) : elt as any as C;
	}

	export function extractActionsFromDom<C>(elt: BaseElement, init: IRegPointer<any>): Action<C>[] {
		const actions = BASIS.extractAttr(elt, 'actions');
		return actions ? elt.findReg(init).mergeLists<Action<C>>(...actions.split(' ')) : [];
	}
}
